import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiPanel, EuiText, EuiButtonIcon, EuiSpacer } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { Persona } from 'api/interfaces/Persona'
import { useGetMasterCampaignPlanQuery, useMasterCampaignPlanEditPersonasMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'

export interface ReorderPersonasFlyoutProps {
    onClose: () => void
}

interface MasterCampaignPlanPageParams {
    masterCampaignPlanId: string
}

const ReorderPersonasFlyout: React.FC<ReorderPersonasFlyoutProps> = ({ onClose }) => {
    const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
    const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)

    const { data: masterCampaignPlan } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId! }, { skip: !masterCampaignPlanId })

    const didFireUpdate = useRef(false)
    const [editPersonasMutation, { isLoading: isEditingPersonas, isSuccess: isEditPersonasSuccess }] = useMasterCampaignPlanEditPersonasMutation()

    const [personas, setPersonas] = useState<Persona[]>(() => {
        const initialPersonas = masterCampaignPlan?.personas || []
        return [...initialPersonas].sort((a, b) => a.rank - b.rank)
    })

    useEffect(() => {
        if (masterCampaignPlan?.personas) {
            const sortedPersonas = [...masterCampaignPlan.personas].sort((a, b) => a.rank - b.rank)
            setPersonas(sortedPersonas)
        }
    }, [masterCampaignPlan?.personas])

    useEffect(() => {
        if (didFireUpdate.current && isEditPersonasSuccess) {
            didFireUpdate.current = false
            onClose()
        }
    }, [didFireUpdate, isEditPersonasSuccess, onClose])

    const onSave = () => {
        if (!masterCampaignPlanId || !currentAccount || !currentOrganization) {
            return
        }

        const updatedPersonas = personas.map((persona, index) => ({
            ...persona,
            rank: index + 1
        }))

        editPersonasMutation({
            organizationId: currentOrganization.id,
            accountId: currentAccount.id,
            masterCampaignPlanId: masterCampaignPlanId,
            personas: updatedPersonas
        })

        didFireUpdate.current = true
    }

    const movePersonaUp = (index: number) => {
        if (index <= 0) return

        const newPersonas = [...personas]
        const temp = newPersonas[index]
        newPersonas[index] = newPersonas[index - 1]
        newPersonas[index - 1] = temp

        setPersonas(newPersonas)
    }

    const movePersonaDown = (index: number) => {
        if (index >= personas.length - 1) return

        const newPersonas = [...personas]
        const temp = newPersonas[index]
        newPersonas[index] = newPersonas[index + 1]
        newPersonas[index + 1] = temp

        setPersonas(newPersonas)
    }

    const onDeletePersona = (personaToDelete: Persona) => {
        setPersonas(personas.filter(p => p.name !== personaToDelete.name || p.rank !== personaToDelete.rank))
    }

    const hasChanges = () => {
        const originalPersonas = masterCampaignPlan?.personas || []
        const originalSorted = [...originalPersonas].sort((a, b) => a.rank - b.rank)

        if (personas.length !== originalSorted.length) {
            return true
        }

        return personas.some((persona, index) => {
            const original = originalSorted[index]
            return !original || persona.name !== original.name || persona.rank !== original.rank
        })
    }

    return (
        <AcFlyout onClose={onClose} size="m">
            <EuiFlyoutHeader>
                <EuiTitle size="m">
                    <h2>Reorder Personas</h2>
                </EuiTitle>
            </EuiFlyoutHeader>
            <EuiFlyoutBody>
                <EuiText size="s" color="subdued">
                    <p>Use the up and down arrows to reorder personas. Click the trash icon to delete a persona.</p>
                </EuiText>
                <EuiSpacer size="m" />

                {personas.map((persona, index) => (
                    <div key={`${persona.name}-${persona.rank}`}>
                        <EuiPanel paddingSize="m">
                            <EuiFlexGroup alignItems="center" justifyContent="spaceBetween" responsive={false}>
                                <EuiFlexItem grow={false}>
                                    <EuiFlexGroup gutterSize="xs" responsive={false}>
                                        <EuiFlexItem grow={false}>
                                            <EuiButtonIcon
                                                iconType="arrowUp"
                                                onClick={() => movePersonaUp(index)}
                                                aria-label={`Move ${persona.name} up`}
                                                isDisabled={index === 0}
                                                size="s"
                                            />
                                        </EuiFlexItem>
                                        <EuiFlexItem grow={false}>
                                            <EuiButtonIcon
                                                iconType="arrowDown"
                                                onClick={() => movePersonaDown(index)}
                                                aria-label={`Move ${persona.name} down`}
                                                isDisabled={index === personas.length - 1}
                                                size="s"
                                            />
                                        </EuiFlexItem>
                                    </EuiFlexGroup>
                                </EuiFlexItem>
                                <EuiFlexItem>
                                    <EuiText size="s">
                                        <strong>{persona.name}</strong>
                                    </EuiText>
                                    <EuiText size="xs" color="subdued">
                                        {persona.description}
                                    </EuiText>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    <EuiButtonIcon
                                        iconType="trash"
                                        onClick={() => onDeletePersona(persona)}
                                        aria-label={`Delete ${persona.name}`}
                                        color="danger"
                                        size="s"
                                    />
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        </EuiPanel>
                        {index < personas.length - 1 && <EuiSpacer size="m" />}
                    </div>
                ))}

                {personas.length === 0 && (
                    <EuiPanel>
                        <EuiText textAlign="center" color="subdued">
                            <p>No personas to display</p>
                        </EuiText>
                    </EuiPanel>
                )}
            </EuiFlyoutBody>
            <EuiFlyoutFooter>
                <EuiFlexGroup direction="row" alignItems="center">
                    <EuiFlexItem grow={false}>
                        <EuiButton onClick={onClose} isDisabled={isEditingPersonas}>
                            Cancel
                        </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButton
                            fill
                            onClick={onSave}
                            isLoading={isEditingPersonas}
                            isDisabled={!hasChanges() || isEditingPersonas}
                        >
                            Save
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFlyoutFooter>
        </AcFlyout>
    )
}

export default ReorderPersonasFlyout 